import { useState, useEffect } from "react";
import { 
  Mail, 
  Send, 
  Inbox, 
  CheckCircle, 
  Clock, 
  User,
  RefreshCw,
  Trash2,
  Reply,
  Megaphone,
  ChevronLeft,
  Search,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("inbox");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [messageableUsers, setMessageableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Compose form
  const [composeForm, setComposeForm] = useState({
    recipient_id: "",
    subject: "",
    message: ""
  });
  
  // Broadcast form
  const [broadcastForm, setBroadcastForm] = useState({
    subject: "",
    message: ""
  });
  
  const [sending, setSending] = useState(false);

  const isAdmin = ["owner", "co_ceo", "super_admin", "staff_admin", "treasurer", "director"].includes(user?.role);

  useEffect(() => {
    fetchMessages();
    fetchMessageableUsers();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const [inboxRes, sentRes] = await Promise.all([
        fetch(`${API}/messages`, { credentials: "include" }),
        fetch(`${API}/messages/sent`, { credentials: "include" })
      ]);
      
      if (inboxRes.ok) {
        const data = await inboxRes.json();
        setMessages(data.messages || []);
      }
      if (sentRes.ok) {
        const data = await sentRes.json();
        setSentMessages(data.messages || []);
      }
    } catch {
      // Silent fail - messages still displayed from cache
    } finally {
      setLoading(false);
    }
  };

  const fetchMessageableUsers = async () => {
    try {
      const response = await fetch(`${API}/users/messageable`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMessageableUsers(data.users || []);
      }
    } catch {
      // Silent fail - user list is non-critical
    }
  };

  const openMessage = async (msg) => {
    setSelectedMessage(msg);
    setMessageOpen(true);
    
    // Mark as read
    if (!msg.read) {
      try {
        await fetch(`${API}/messages/${msg.id}`, { credentials: "include" });
        // Update local state
        setMessages(prev => prev.map(m => 
          m.id === msg.id ? { ...m, read: true } : m
        ));
      } catch {
        // Silent fail - read status is non-critical
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!composeForm.recipient_id || !composeForm.message.trim()) {
      toast.error("Please select a recipient and write a message");
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${API}/messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(composeForm)
      });
      
      if (response.ok) {
        toast.success("Message sent!");
        setComposeOpen(false);
        setComposeForm({ recipient_id: "", subject: "", message: "" });
        fetchMessages();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastForm.message.trim()) {
      toast.error("Please write a message");
      return;
    }
    
    setSending(true);
    try {
      const response = await fetch(`${API}/messages/broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(broadcastForm)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setBroadcastOpen(false);
        setBroadcastForm({ subject: "", message: "" });
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to send broadcast");
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      const response = await fetch(`${API}/messages/${messageId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Message deleted");
        setMessages(prev => prev.filter(m => m.id !== messageId));
        setSentMessages(prev => prev.filter(m => m.id !== messageId));
        setMessageOpen(false);
      }
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleReply = (msg) => {
    setMessageOpen(false);
    setComposeForm({
      recipient_id: msg.sender_id,
      subject: msg.subject?.startsWith("Re:") ? msg.subject : `Re: ${msg.subject}`,
      message: ""
    });
    setComposeOpen(true);
  };

  const unreadCount = messages.filter(m => !m.read).length;
  
  const filteredMessages = (activeTab === "inbox" ? messages : sentMessages).filter(msg => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      msg.subject?.toLowerCase().includes(query) ||
      msg.message?.toLowerCase().includes(query) ||
      msg.sender_name?.toLowerCase().includes(query) ||
      msg.recipient_name?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Mail className="text-[#FFD700]" size={28} />
            Private Messages
            {unreadCount > 0 && (
              <Badge className="bg-[#FFD700] text-[#0a0500] ml-2">{unreadCount}</Badge>
            )}
          </h1>
          <p className="text-[#E5C558] mt-1">Your private conversations • Only you and the recipient can see these</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setComposeOpen(true)}
            className="bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold hover:from-[#E5C558] hover:to-[#FFD700]"
            data-testid="compose-btn"
          >
            <Send size={16} className="mr-2" />
            Direct Message
          </Button>
          {isAdmin && (
            <Button
              onClick={() => setBroadcastOpen(true)}
              variant="outline"
              className="border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10"
              data-testid="broadcast-btn"
            >
              <Megaphone size={16} className="mr-2" />
              Team Broadcast
            </Button>
          )}
          <Button
            onClick={fetchMessages}
            variant="outline"
            className="border-[#27272A] text-[#E5C558] hover:text-[#FFD700]"
          >
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Privacy Notice */}
      <div 
        className="flex items-center gap-3 p-3 rounded-lg"
        style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.2)' }}
      >
        <div className="w-8 h-8 rounded-full bg-[#FFD700]/20 flex items-center justify-center flex-shrink-0">
          <Mail size={16} className="text-[#FFD700]" />
        </div>
        <p className="text-sm text-[#E5C558]">
          <strong className="text-[#FFD700]">Privacy:</strong> Direct messages are private between you and the recipient. 
          Team broadcasts are visible to all staff members.
        </p>
      </div>

      {/* Tabs & Search */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex rounded-lg p-1" style={{ background: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(8px)' }}>
          <button
            onClick={() => setActiveTab("inbox")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === "inbox" 
                ? "bg-[#FFD700] text-[#0a0500]" 
                : "text-[#E5C558] hover:text-[#FFFFFF]"
            }`}
          >
            <Inbox size={16} className="inline mr-2" />
            Inbox
            {unreadCount > 0 && (
              <span className="ml-2 bg-[#0a0500] text-[#FFD700] text-xs px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("sent")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === "sent" 
                ? "bg-[#FFD700] text-[#0a0500]" 
                : "text-[#E5C558] hover:text-[#FFFFFF]"
            }`}
          >
            <Send size={16} className="inline mr-2" />
            Sent
          </button>
        </div>
        
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E5C558]" size={18} />
          <Input
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1A1A1A] border-[#27272A] text-[#FFFFFF]"
            style={{ background: 'rgba(26, 26, 26, 0.8)' }}
          />
        </div>
      </div>

      {/* Messages List - Cinematic Glass Card */}
      <Card 
        style={{
          background: 'rgba(18, 18, 18, 0.8)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)'
        }}
      >
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-12">
              <Mail size={48} className="text-[#27272A] mx-auto mb-4" />
              <p className="text-[#C9C9C9]">
                {activeTab === "inbox" ? "No messages in your inbox" : "No sent messages"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[#27272A]">
              {filteredMessages.map((msg) => (
                <div
                  key={msg.id}
                  onClick={() => openMessage(msg)}
                  className={`flex items-center gap-4 p-4 cursor-pointer transition-all hover:bg-[#1A1A1A]/80 ${
                    !msg.read && activeTab === "inbox" ? "bg-[#D4AF37]/5" : ""
                  }`}
                  data-testid={`message-${msg.id}`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {msg.sender_picture ? (
                      <img 
                        src={msg.sender_picture} 
                        alt={msg.sender_name} 
                        className="w-10 h-10 rounded-full border-2 border-[#27272A]"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                        <User className="text-[#D4AF37]" size={20} />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium truncate ${!msg.read && activeTab === "inbox" ? "text-[#FFFFFF]" : "text-[#C9C9C9]"}`}>
                        {activeTab === "inbox" ? msg.sender_name : msg.recipient_name}
                      </p>
                      {msg.type === "broadcast" && (
                        <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] text-xs">Broadcast</Badge>
                      )}
                      {msg.type === "system" && (
                        <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs">System</Badge>
                      )}
                    </div>
                    <p className={`text-sm truncate ${!msg.read && activeTab === "inbox" ? "text-[#FFFFFF] font-medium" : "text-[#C9C9C9]"}`}>
                      {msg.subject}
                    </p>
                    <p className="text-xs text-[#71717A] truncate mt-1">
                      {msg.message?.substring(0, 80)}...
                    </p>
                  </div>
                  
                  {/* Time & Status */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-[#C9C9C9]">{formatDate(msg.created_at)}</p>
                    {!msg.read && activeTab === "inbox" && (
                      <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 ml-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Message Dialog */}
      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent className="bg-[#141416] border-[#27272A] text-[#FAFAFA] max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedMessage && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-xl">{selectedMessage.subject}</DialogTitle>
                  <div className="flex gap-2">
                    {selectedMessage.sender_id !== "system" && activeTab === "inbox" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReply(selectedMessage)}
                        className="border-[#27272A] text-[#A1A1AA] hover:text-[#D4AF37]"
                      >
                        <Reply size={14} className="mr-1" />
                        Reply
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(selectedMessage.id)}
                      className="border-[#27272A] text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Sender Info */}
                <div className="flex items-center gap-3 p-3 bg-[#1A1A1D] rounded-lg">
                  {selectedMessage.sender_picture ? (
                    <img 
                      src={selectedMessage.sender_picture} 
                      alt={selectedMessage.sender_name} 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <User className="text-[#D4AF37]" size={20} />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{selectedMessage.sender_name}</p>
                    <p className="text-xs text-[#71717A]">
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </p>
                  </div>
                  {selectedMessage.type === "broadcast" && (
                    <Badge className="bg-[#3B82F6]/20 text-[#3B82F6] ml-auto">Broadcast</Badge>
                  )}
                </div>
                
                {/* Message Content */}
                <div className="p-4 bg-[#1A1A1D] rounded-lg">
                  <p className="whitespace-pre-wrap text-[#E4E4E7] leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Compose Message Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="bg-[#141416] border-[#27272A] text-[#FAFAFA] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Send className="text-[#D4AF37]" size={20} />
              Compose Message
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSendMessage} className="space-y-4 py-4">
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">To</label>
              <Select 
                value={composeForm.recipient_id} 
                onValueChange={(v) => setComposeForm({...composeForm, recipient_id: v})}
              >
                <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                  <SelectValue placeholder="Select recipient" />
                </SelectTrigger>
                <SelectContent className="bg-[#141416] border-[#27272A]">
                  {messageableUsers.map((u) => (
                    <SelectItem 
                      key={u.user_id} 
                      value={u.user_id}
                      className="text-[#FAFAFA] focus:bg-[#D4AF37]/20"
                    >
                      <div className="flex items-center gap-2">
                        <span>{u.name}</span>
                        <span className="text-[#71717A] text-xs">({u.role})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Subject</label>
              <Input
                value={composeForm.subject}
                onChange={(e) => setComposeForm({...composeForm, subject: e.target.value})}
                placeholder="Message subject"
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Message</label>
              <Textarea
                value={composeForm.message}
                onChange={(e) => setComposeForm({...composeForm, message: e.target.value})}
                placeholder="Write your message..."
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] min-h-[150px]"
                required
              />
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setComposeOpen(false)}
                className="border-[#27272A] text-[#A1A1AA]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold"
              >
                {sending ? "Sending..." : "Send Message"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent className="bg-[#141416] border-[#27272A] text-[#FAFAFA] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Megaphone className="text-[#D4AF37]" size={20} />
              Broadcast to All Staff
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleBroadcast} className="space-y-4 py-4">
            <div className="p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg">
              <p className="text-sm text-[#F59E0B]">
                This message will be sent to all active staff members.
              </p>
            </div>
            
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Subject</label>
              <Input
                value={broadcastForm.subject}
                onChange={(e) => setBroadcastForm({...broadcastForm, subject: e.target.value})}
                placeholder="Announcement subject"
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              />
            </div>
            
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Message</label>
              <Textarea
                value={broadcastForm.message}
                onChange={(e) => setBroadcastForm({...broadcastForm, message: e.target.value})}
                placeholder="Write your announcement..."
                className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] min-h-[150px]"
                required
              />
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBroadcastOpen(false)}
                className="border-[#27272A] text-[#A1A1AA]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold"
              >
                {sending ? "Sending..." : "Send Broadcast"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Messages;
